import { booleanAttr } from "@wiki/core";
import type { OrderedListStyle } from "@wiki/core";
import type { Node as ProseMirrorNode, NodeType } from "prosemirror-model";
import { liftListItem, sinkListItem, splitListItem, wrapInList } from "prosemirror-schema-list";
import { TextSelection, type Command, type EditorState, type Transaction } from "prosemirror-state";

import { findParent, findParentOfAnyType, itemTypeFor, nodes } from "../schema";
import { swapWithSibling } from "./util";

const LIST_TYPES = [nodes.bulletList, nodes.orderedList, nodes.checkboxList] as const;
const ITEM_TYPES = [nodes.listItem, nodes.checkboxItem] as const;

export interface ListSpec {
  type: NodeType;
  attrs?: Record<string, unknown>;
}

export const BULLET_LIST: ListSpec = { type: nodes.bulletList };
export const TODO_LIST: ListSpec = { type: nodes.checkboxList };
export const orderedList = (listStyle: OrderedListStyle = "number", order = 1): ListSpec => ({
  type: nodes.orderedList,
  attrs: { order, listStyle },
});

/** The list wrapping the selection, if any. */
export function currentList(
  state: EditorState,
): { type: NodeType; attrs: Record<string, unknown> } | null {
  const found = findParentOfAnyType(state.selection.$from, LIST_TYPES);
  return found ? { type: found.node.type, attrs: found.node.attrs } : null;
}

/** The item type to hand to the `prosemirror-schema-list` commands. */
export function currentItemType(state: EditorState): NodeType | null {
  const found = findParentOfAnyType(state.selection.$from, ITEM_TYPES);
  return found ? found.node.type : null;
}

function attrsMatch(
  attrs: Record<string, unknown>,
  expected: Record<string, unknown> = {},
): boolean {
  return Object.entries(expected).every(([key, value]) => attrs[key] === value);
}

export function listActive(state: EditorState, spec: ListSpec): boolean {
  const list = currentList(state);
  return list !== null && list.type === spec.type && attrsMatch(list.attrs, spec.attrs);
}

/**
 * Change an existing list (and its items) to another kind in place. Retyping
 * rather than unwrapping-and-rewrapping keeps nested content and the caret
 * where they are, and it is what makes "bulleted → task list" a single change.
 */
function convertList(spec: ListSpec, listPos: number): Command {
  return (state, dispatch) => {
    const list = state.doc.nodeAt(listPos);
    if (!list) return false;
    const itemType = itemTypeFor(spec.type);
    if (dispatch) {
      // The list and its items change type together: a checkbox list holding
      // plain list items is not a valid intermediate state, so the whole node
      // is rebuilt rather than retyped in place.
      const items: ProseMirrorNode[] = [];
      list.forEach((child) => {
        items.push(
          child.type === itemType
            ? child
            : itemType.createChecked(
                itemType === nodes.checkboxItem ? { checked: false } : null,
                child.content,
              ),
        );
      });
      const converted = spec.type.createChecked({ ...spec.attrs }, items);
      const tr: Transaction = state.tr.replaceWith(listPos, listPos + list.nodeSize, converted);
      // Retyping does not change any node's size, so the caret belongs exactly
      // where it was; mapping through a whole-node replacement would push it
      // out of the list.
      const { from, to } = state.selection;
      tr.setSelection(
        TextSelection.create(
          tr.doc,
          Math.min(from, tr.doc.content.size),
          Math.min(to, tr.doc.content.size),
        ),
      );
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

/**
 * `^⇧7`–`^⇧9` and the list items in the slash and selection menus: wrap, or
 * convert, or — when the same kind is already active — lift back out.
 */
export function toggleList(spec: ListSpec): Command {
  return (state, dispatch, view) => {
    const found = findParentOfAnyType(state.selection.$from, LIST_TYPES);
    if (found) {
      if (found.node.type === spec.type && attrsMatch(found.node.attrs, spec.attrs)) {
        return liftListItem(itemTypeFor(found.node.type))(state, dispatch, view);
      }
      return convertList(spec, found.pos)(state, dispatch, view);
    }
    return wrapInList(spec.type, spec.attrs ?? null)(state, dispatch, view);
  };
}

/** `Tab` / `Mod-]` inside a list item. */
export const indentListItem: Command = (state, dispatch, view) => {
  const itemType = currentItemType(state);
  if (!itemType) return false;
  return sinkListItem(itemType)(state, dispatch, view);
};

/** `⇧-Tab` / `Mod-[` inside a list item. */
export const outdentListItem: Command = (state, dispatch, view) => {
  const itemType = currentItemType(state);
  if (!itemType) return false;
  return liftListItem(itemType)(state, dispatch, view);
};

/**
 * `Enter` and `Backspace` on an empty item: leave the list instead of making
 * another empty item (`editor.md` §10, "on an empty item, lift out of the
 * list"). A nested item outdents one level per press, so repeated presses walk
 * out of a deep list a level at a time and then out of the list altogether.
 *
 * Only an item whose whole content is this one empty block lifts. An empty
 * paragraph that follows other content inside the item is a block of its own
 * and splits normally.
 */
export const liftEmptyListItem: Command = (state, dispatch, view) => {
  const { $from, empty } = state.selection;
  if (!empty || !$from.parent.isTextblock || $from.parent.content.size > 0) return false;
  const found = findParentOfAnyType($from, ITEM_TYPES);
  if (found === null || found.node.childCount > 1) return false;
  return liftListItem(found.node.type)(state, dispatch, view);
};

/** `Enter`: split the item; an empty item lifts out of the list instead. */
export const splitListItemCommand: Command = (state, dispatch, view) => {
  const itemType = currentItemType(state);
  if (!itemType) return false;
  const attrs = itemType === nodes.checkboxItem ? { checked: false } : undefined;
  return splitListItem(itemType, attrs)(state, dispatch, view);
};

/** `Alt-↑` / `Alt-↓`: move the item among its siblings. */
export function moveListItem(direction: -1 | 1): Command {
  return (state, dispatch) => {
    const found = findParent(state.selection.$from, (node) => ITEM_TYPES.includes(node.type));
    if (!found) return false;
    return swapWithSibling(state, found, direction, dispatch);
  };
}

/** `Mod-Enter` inside a task item, and the click target in read mode (§14). */
export const toggleTaskChecked: Command = (state, dispatch) => {
  const found = findParent(state.selection.$from, (node) => node.type === nodes.checkboxItem);
  if (!found) return false;
  if (dispatch) {
    dispatch(state.tr.setNodeAttribute(found.pos, "checked", !booleanAttr(found.node, "checked")));
  }
  return true;
};

/** Used by the checkbox node view, which knows the item's position already. */
export function setTaskChecked(pos: number, checked: boolean): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (node?.type !== nodes.checkboxItem) return false;
    if (dispatch) dispatch(state.tr.setNodeAttribute(pos, "checked", checked));
    return true;
  };
}
